import type {
  TableBodyProps,
  TableContainerProps,
  TableFooterProps,
  TableHeadProps,
  TableProps,
  TableRowProps,
} from "@mui/material";
import {
  type Context,
  type Dispatch,
  type Provider,
  type ReactNode,
  type SetStateAction,
  createContext,
  createElement,
  useContext,
  useMemo,
} from "react";

export interface ITableVirtualizedSelectContext<Data extends { _id: string } = { _id: string }> {
  TableProps?: TableProps;
  TableBodyProps?: TableBodyProps;
  TableContainerProps?: TableContainerProps;
  TableFootProps?: TableFooterProps;
  TableHeadProps?: TableHeadProps;
  TableRowProps?: TableRowProps;
  hiddenCheckbox?: boolean;
  selectedItems: "all" | readonly Data[];
  setSelectedItems: Dispatch<SetStateAction<"all" | readonly Data[]>>;
  allCheckboxProps: undefined | { checked?: boolean; indeterminate?: boolean };
  isChecked: (item: Data) => boolean;
  toggleCheckbox: (item: Data) => void;
  toggleAllCheckbox: () => void;
}

const context = createContext<ITableVirtualizedSelectContext>({
  selectedItems: [],
  setSelectedItems: () => undefined,
  allCheckboxProps: undefined,
  isChecked: () => false,
  toggleCheckbox: () => undefined,
  toggleAllCheckbox: () => undefined,
});

export const useTableVirtualizedSelectContext = <Data extends { _id: string }>() =>
  useContext(context as unknown as Context<ITableVirtualizedSelectContext<Data>>);

export const TableVirtualizedSelectProvider = <Data extends { _id: string }>({
  children,
  ...props
}: ITableVirtualizedSelectContext<Data> & { children: ReactNode }) => {
  const value = useMemo(
    () => ({ ...props }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.TableProps,
      props.TableBodyProps,
      props.TableContainerProps,
      props.TableFootProps,
      props.TableHeadProps,
      props.TableRowProps,
      props.hiddenCheckbox,
      props.selectedItems,
      props.setSelectedItems,
      props.allCheckboxProps,
      props.isChecked,
      props.toggleCheckbox,
      props.toggleAllCheckbox,
    ]
  );

  return createElement(
    context.Provider as unknown as Provider<ITableVirtualizedSelectContext<Data>>,
    { value },
    children
  );
};
